/**
 * Within-tier ordering keys.
 *
 * A song's position inside its rating tier is a sortable string, not a number. The reason is
 * `rating.updated_at`: the dashboard's "recently rated" panel reads it (apps/api/src/routes/
 * dashboard.ts), so integer positions would mean moving one row to the top of a 200-song tier
 * touches 200 rows, and the panel fills with songs the user did not rate. A key that can be
 * generated *between* its neighbours moves one row by writing one row.
 *
 * Hand-rolled rather than pulled from npm. `packages/core` has no runtime dependencies and that
 * is deliberate — it is the arithmetic every other package trusts. The generation itself is
 * thirty lines; the edge cases are where this goes wrong, so they are tested exhaustively in
 * ordering.test.ts rather than assumed.
 *
 * Keys are compared with plain lexicographic `<`, which is also what Postgres does for `text`
 * under the C collation used by the ordering index — the client and the server must agree on the
 * order of two keys or the table renders differently from the query that paginated it.
 */
/**
 * Base 62, in ASCII order: digits, then upper case, then lower case. The order of this string IS
 * the sort order, so it must stay ASCII-ascending — `'0' < 'A' < 'a'` — or lexicographic string
 * comparison stops agreeing with the alphabet's own index.
 */
const DIGITS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
const ZERO = DIGITS[0];
/** Keys are the fractional part of a base-62 number: `"V"` means 0.V, `"1V"` means 0.1V. */
function midpoint(a, b) {
    if (b !== null && a >= b) {
        throw new Error(`ordering: ${JSON.stringify(a)} is not below ${JSON.stringify(b)}`);
    }
    /*
     * No key may end in the zero digit. `"1"` and `"10"` denote the same fraction, so a trailing
     * zero would make two different strings name one position and leave no room between them —
     * the one state this generator cannot recover from.
     */
    if (a.endsWith(ZERO) || (b !== null && b.endsWith(ZERO))) {
        throw new Error("ordering: keys may not end in the zero digit");
    }
    if (b !== null) {
        // Descend through the shared prefix: the interesting digits are the first ones that differ.
        let n = 0;
        while ((a[n] ?? ZERO) === b[n])
            n += 1;
        if (n > 0)
            return b.slice(0, n) + midpoint(a.slice(n), b.slice(n));
    }
    const digitA = a === "" ? 0 : DIGITS.indexOf(a[0]);
    const digitB = b === null ? DIGITS.length : DIGITS.indexOf(b[0]);
    // Room between the two leading digits: take the one in the middle and stop. This is the case
    // that keeps keys short, and it is the case that fires for almost every real insert.
    if (digitB - digitA > 1)
        return DIGITS[Math.round(0.5 * (digitA + digitB))];
    // The leading digits are adjacent, so the answer is one character longer than its neighbour.
    if (b !== null && b.length > 1)
        return b.slice(0, 1);
    return DIGITS[digitA] + midpoint(a.slice(1), null);
}
/**
 * A key that sorts strictly between `a` and `b`. `null` means unbounded on that side, so
 * `keyBetween(null, null)` is the first key in an empty tier and `keyBetween(last, null)` appends.
 */
export function keyBetween(a, b) {
    return midpoint(a ?? "", b);
}
/** `n` keys, evenly spread between `a` and `b`, in ascending order. */
export function keysBetween(a, b, n) {
    if (n <= 0)
        return [];
    if (n === 1)
        return [keyBetween(a, b)];
    // Bisect rather than walking from one end: appending n times in a row grows the last key by a
    // character every few steps, and a group of 200 would end up with keys of wildly uneven length.
    const half = Math.floor(n / 2);
    const mid = keyBetween(a, b);
    return [...keysBetween(a, mid, half), mid, ...keysBetween(mid, b, n - half - 1)];
}
/** Keys longer than this are rebalanced. Reached only by repeatedly inserting into one gap. */
export const REBALANCE_AT = 20;
/**
 * Works out the smallest set of key writes that makes `current` sort into the order it is given
 * in.
 *
 * `current[i]` is the key row `i` holds today, or `null` if it has never been placed. The rows
 * are already in the order the user wants; the job is to decide which of them actually have to
 * change.
 *
 * Rows whose existing keys are already ascending are left alone — that is what makes dragging one
 * row inside an ordered group a single UPDATE. The set kept is a longest increasing subsequence,
 * so the answer is not merely small but minimal: no other assignment rewrites fewer rows.
 *
 * A group nobody has ordered yet arrives as all-nulls, keeps nothing, and is assigned `n` evenly
 * spread keys in one go. That is the transition the design describes — a tie group shows a shared
 * rank "until the user drags its rows into order", at which point the whole group is ordered.
 */
export function planOrder(current) {
    const n = current.length;
    if (n === 0)
        return [];
    // Longest strictly-increasing subsequence over the rows that already hold a key. Strict, so two
    // rows can never be kept on the same key — equal keys would leave the tie the ordering exists
    // to break.
    const best = new Array(n).fill(0);
    const previous = new Array(n).fill(-1);
    let endOfBest = -1;
    for (let i = 0; i < n; i += 1) {
        if (current[i] === null)
            continue;
        best[i] = 1;
        for (let j = 0; j < i; j += 1) {
            if (current[j] === null)
                continue;
            if (current[j] < current[i] && best[j] + 1 > best[i]) {
                best[i] = best[j] + 1;
                previous[i] = j;
            }
        }
        if (endOfBest < 0 || best[i] > best[endOfBest])
            endOfBest = i;
    }
    const kept = new Set();
    for (let i = endOfBest; i >= 0; i = previous[i]) {
        kept.add(i);
        if (previous[i] === -1)
            break;
    }
    // Everything not kept is rewritten, in runs between the anchors that survived.
    const out = [];
    let i = 0;
    while (i < n) {
        if (kept.has(i)) {
            i += 1;
            continue;
        }
        let end = i;
        while (end < n && !kept.has(end))
            end += 1;
        const before = i > 0 ? current[i - 1] : null;
        const after = end < n ? current[end] : null;
        const keys = keysBetween(before, after, end - i);
        for (let k = 0; k < keys.length; k += 1)
            out.push({ index: i + k, key: keys[k] });
        i = end;
    }
    return out;
}
/**
 * Fresh, evenly spread keys for a whole tier — the `rebalance` maintenance path.
 *
 * Called when a key has grown past REBALANCE_AT, which happens only by inserting into the same
 * gap over and over. The order it produces is the order it was given, so rebalancing is invisible:
 * the tier reads identically afterwards.
 */
export function rebalance(n) {
    return keysBetween(null, null, n);
}
/** True when a tier holds a key long enough to be worth rebalancing. */
export function needsRebalance(keys) {
    return keys.some((k) => k !== null && k.length > REBALANCE_AT);
}
//# sourceMappingURL=ordering.js.map